import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { tick } from 'svelte';
import Button from './svelte-button.svelte';

var root = $.from_html(`<div><!></div>`);
var root_1 = $.from_html(`<div><!> <!></div>`);

const $$css = {
	hash: 'svelte-i2zi8y',
	code: '.ui-menu-open-down {top:calc(100% + 8px);bottom:auto;}.ui-menu-open-up {top:auto;bottom:calc(100% + 8px);}'
};

export default function Svelte_dropdown_menu($$anchor, $$props) {
	$.push($$props, true);
	$.append_styles($$anchor, $$css);

	const wrapperClass = $.prop($$props, 'wrapperClass', 7, ''),
		menuClass = $.prop($$props, 'menuClass', 7, ''),
		triggerAriaLabel = $.prop($$props, 'triggerAriaLabel', 7, ''),
		menuRole = $.prop($$props, 'menuRole', 7, 'menu'),
		triggerText = $.prop($$props, 'triggerText', 7, ''),
		triggerVariant = $.prop($$props, 'triggerVariant', 7, 'default'),
		triggerClassName = $.prop($$props, 'triggerClassName', 7, ''),
		floating = $.prop($$props, 'floating', 7, false),
		onOpen = $.prop($$props, 'onOpen', 7, undefined),
		onClose = $.prop($$props, 'onClose', 7, undefined),
		trigger = $.prop($$props, 'trigger', 7, undefined),
		children = $.prop($$props, 'children', 7, undefined);

	let isOpen = $.state(false);
	let wrapperEl = $.state(null);
	let triggerEl = $.state(null);
	let menuEl = $.state(null);
	let menuPlacement = $.state('down');
	let floatingStyle = $.state('');
	let portalHost = $.state(null);

	function portal(node) {
		if (!floating() || typeof document === 'undefined') {
			return {
				destroy() {
					return undefined;
				}
			};
		}

		$.set(portalHost, $.get(wrapperEl)?.closest('dialog') ?? document.body, true);
		$.get(portalHost).appendChild(node);

		return {
			destroy() {
				if (node.parentNode) {
					node.parentNode.removeChild(node);
				}

				$.set(portalHost, null);
			}
		};
	}

	async function updateMenuPlacement() {
		if (!$.get(isOpen)) {
			return;
		}

		await tick();

		if (!$.get(triggerEl) || !$.get(menuEl)) {
			return;
		}

		const triggerRect = $.get(triggerEl).getBoundingClientRect();
		const menuRect = $.get(menuEl).getBoundingClientRect();
		const spaceBelow = window.innerHeight - triggerRect.bottom - 8;
		const spaceAbove = triggerRect.top - 8;

		if (menuRect.height > spaceBelow && spaceAbove > spaceBelow) {
			$.set(menuPlacement, 'up');
		} else {
			$.set(menuPlacement, 'down');
		}

		if (!floating()) {
			$.set(floatingStyle, '');

			return;
		}

		const menuWidth = menuRect.width;
		const margin = 8;
		const maxLeft = Math.max(margin, window.innerWidth - menuWidth - margin);
		const alignedLeft = Math.min(maxLeft, Math.max(margin, triggerRect.right - menuWidth));

		const top = $.get(menuPlacement) === 'up'
			? Math.max(margin, triggerRect.top - menuRect.height - 8)
			: Math.min(window.innerHeight - menuRect.height - margin, triggerRect.bottom + 8);

		$.set(
			floatingStyle,
			[
				'position:fixed',
				`left:${Math.round(alignedLeft)}px`,
				`top:${Math.round(top)}px`,
				'right:auto',
				'bottom:auto'
			].join(';'),
			true
		);
	}

	function focusWithoutScroll(element) {
		element?.focus({ preventScroll: true });
	}

	function closeMenu() {
		if (!$.get(isOpen)) {
			return;
		}

		$.set(isOpen, false);
		onClose()?.();
	}

	function toggleMenu() {
		$.set(isOpen, !$.get(isOpen));

		if ($.get(isOpen)) {
			onOpen()?.();
			void updateMenuPlacement();

			return;
		}

		onClose()?.();
	}

	function getFocusableItems() {
		if (!$.get(menuEl)) {
			return [];
		}

		return Array.from($.get(menuEl).querySelectorAll('[role="menuitem"], button, a[href], [tabindex]:not([tabindex="-1"])')).filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-disabled') !== 'true');
	}

	function focusBoundary(which) {
		const items = getFocusableItems();

		if (items.length === 0) {
			return;
		}

		if (which === 'first') {
			focusWithoutScroll(items[0]);

			return;
		}

		focusWithoutScroll(items[items.length - 1]);
	}

	function moveFocus(direction) {
		const items = getFocusableItems();

		if (items.length === 0) {
			return;
		}

		const active = document.activeElement;
		const currentIndex = items.findIndex((item) => item === active);

		if (currentIndex < 0) {
			focusBoundary(direction === 1 ? 'first' : 'last');

			return;
		}

		const nextIndex = (currentIndex + direction + items.length) % items.length;

		items[nextIndex].focus();
	}

	function handleGlobalPointerDown(event) {
		if (!$.get(isOpen)) {
			return;
		}

		const target = event.target;

		if (!(target instanceof Node)) {
			return;
		}

		if ($.get(wrapperEl)?.contains(target) || $.get(menuEl)?.contains(target)) {
			return;
		}

		closeMenu();
	}

	function handleGlobalKeyDown(event) {
		if (!$.get(isOpen) || event.key !== 'Escape') {
			return;
		}

		event.preventDefault();
		closeMenu();
		focusWithoutScroll($.get(triggerEl));
	}

	function handleTriggerKeyDown(event) {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			$.set(isOpen, true);
			void updateMenuPlacement();
			queueMicrotask(() => focusBoundary('first'));

			return;
		}

		if (event.key === 'ArrowUp') {
			event.preventDefault();
			$.set(isOpen, true);
			void updateMenuPlacement();
			queueMicrotask(() => focusBoundary('last'));
		}
	}

	function handleMenuKeyDown(event) {
		if (!$.get(isOpen)) {
			return;
		}

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			moveFocus(1);

			return;
		}

		if (event.key === 'ArrowUp') {
			event.preventDefault();
			moveFocus(-1);

			return;
		}

		if (event.key === 'Enter') {
			const active = document.activeElement;

			if (active instanceof HTMLElement && $.get(menuEl)?.contains(active)) {
				event.preventDefault();
				active.click();
			}
		}
	}

	$.user_effect(() => {
		if (typeof window === 'undefined' || !$.get(isOpen)) {
			return;
		}

		window.addEventListener('pointerdown', handleGlobalPointerDown);
		window.addEventListener('keydown', handleGlobalKeyDown);
		window.addEventListener('resize', updateMenuPlacement);
		window.addEventListener('scroll', updateMenuPlacement, true);
		queueMicrotask(() => focusBoundary('first'));

		queueMicrotask(() => {
			void updateMenuPlacement();
		});

		return () => {
			window.removeEventListener('pointerdown', handleGlobalPointerDown);
			window.removeEventListener('keydown', handleGlobalKeyDown);
			window.removeEventListener('resize', updateMenuPlacement);
			window.removeEventListener('scroll', updateMenuPlacement, true);
		};
	});

	var $$exports = {
		get wrapperClass() {
			return wrapperClass();
		},

		set wrapperClass($$value = '') {
			wrapperClass($$value);
			$.flush();
		},

		get menuClass() {
			return menuClass();
		},

		set menuClass($$value = '') {
			menuClass($$value);
			$.flush();
		},

		get triggerAriaLabel() {
			return triggerAriaLabel();
		},

		set triggerAriaLabel($$value = '') {
			triggerAriaLabel($$value);
			$.flush();
		},

		get menuRole() {
			return menuRole();
		},

		set menuRole($$value = 'menu') {
			menuRole($$value);
			$.flush();
		},

		get triggerText() {
			return triggerText();
		},

		set triggerText($$value = '') {
			triggerText($$value);
			$.flush();
		},

		get triggerVariant() {
			return triggerVariant();
		},

		set triggerVariant($$value = 'default') {
			triggerVariant($$value);
			$.flush();
		},

		get triggerClassName() {
			return triggerClassName();
		},

		set triggerClassName($$value = '') {
			triggerClassName($$value);
			$.flush();
		},

		get floating() {
			return floating();
		},

		set floating($$value = false) {
			floating($$value);
			$.flush();
		},

		get onOpen() {
			return onOpen();
		},

		set onOpen($$value = undefined) {
			onOpen($$value);
			$.flush();
		},

		get onClose() {
			return onClose();
		},

		set onClose($$value = undefined) {
			onClose($$value);
			$.flush();
		},

		get trigger() {
			return trigger();
		},

		set trigger($$value = undefined) {
			trigger($$value);
			$.flush();
		},

		get children() {
			return children();
		},

		set children($$value = undefined) {
			children($$value);
			$.flush();
		}
	};

	var div = root_1();

	$.event('beforeunload', $.window, closeMenu);

	var node_1 = $.child(div);

	{
		let $0 = $.derived(() => $.get(isOpen) ? 'true' : 'false');

		Button(node_1, {
			type: 'button',
			get variant() {
				return triggerVariant();
			},

			get className() {
				return triggerClassName();
			},

			get ariaLabel() {
				return triggerAriaLabel();
			},

			get ariaHaspopup() {
				return menuRole();
			},

			get ariaExpanded() {
				return $.get($0);
			},
			handleMouseDown: (event) => event.preventDefault(),
			handlePress: toggleMenu,
			handleKeyPress: handleTriggerKeyDown,
			get element() {
				return $.get(triggerEl);
			},

			set element($$value) {
				$.set(triggerEl, $$value, true);
			},

			children: ($$anchor, $$slotProps) => {
				var fragment = $.comment();
				var node_2 = $.first_child(fragment);

				{
					var consequent = ($$anchor) => {
						var fragment_1 = $.comment();
						var node_3 = $.first_child(fragment_1);

						$.snippet(node_3, trigger);
						$.append($$anchor, fragment_1);
					};

					var alternate = ($$anchor) => {
						var text = $.text();

						$.template_effect(() => $.set_text(text, triggerText() || '･･･'));
						$.append($$anchor, text);
					};

					$.if(node_2, ($$render) => {
						if (trigger()) $$render(consequent); else $$render(alternate, -1);
					});
				}

				$.append($$anchor, fragment);
			},
			$$slots: { default: true }
		});
	}

	var node_4 = $.sibling(node_1, 2);

	{
		var consequent_1 = ($$anchor) => {
			var div_1 = root();
			var node_5 = $.child(div_1);

			$.snippet(node_5, () => children() ?? $.noop, () => closeMenu);
			$.reset(div_1);
			$.action(div_1, ($$node) => portal?.($$node));
			$.bind_this(div_1, ($$value) => $.set(menuEl, $$value), () => $.get(menuEl));

			$.template_effect(
				($0) => {
					$.set_class(div_1, 1, $0);
					$.set_attribute(div_1, 'role', menuRole());
					$.set_style(div_1, floating() ? $.get(floatingStyle) : undefined);
				},
				[
					() => $.clsx(`${menuClass()} ${$.get(menuPlacement) === 'up' ? 'ui-menu-open-up' : 'ui-menu-open-down'}`.trim())
				]
			);

			$.delegated('pointerdown', div_1, (event) => {
				event.stopPropagation();
			});

			$.delegated('keydown', div_1, handleMenuKeyDown);
			$.append($$anchor, div_1);
		};

		$.if(node_4, ($$render) => {
			if ($.get(isOpen)) $$render(consequent_1);
		});
	}

	$.reset(div);
	$.bind_this(div, ($$value) => $.set(wrapperEl, $$value), () => $.get(wrapperEl));
	$.template_effect(() => $.set_class(div, 1, $.clsx(wrapperClass())));
	$.append($$anchor, div);

	return $.pop($$exports);
}

$.delegate(['pointerdown', 'keydown']);

$.create_custom_element(
	Svelte_dropdown_menu,
	{
		wrapperClass: {},
		menuClass: {},
		triggerAriaLabel: {},
		menuRole: {},
		triggerText: {},
		triggerVariant: {},
		triggerClassName: {},
		floating: {},
		onOpen: {},
		onClose: {},
		trigger: {},
		children: {}
	},
	[],
	[],
	{ mode: 'open' }
);